import React, { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import moment from "moment";
import { Modal, Table, Typography, Button, Tag, Dropdown } from "antd";
import NewTariff from "./NewTariff";
import EditTariff from "./EditTariff";
import EditTariffVersion from "./EditTariffVersion";
import NewVersion from "./NewVersion";
import FormHeaderWrapper from "../forms/FormHeader.style";
import {
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

import RestrictedComponent from "../../components/util/RestrictedComponent";

const { Title, Text } = Typography;

const TariffsList = (props) => {
  const {
    items,
    loading,
    changeLoading,
    deleteLoading,
    fetchPage,
    loadRecord,
    reset,
    deleteRecord,
    createRecord,
    updateRecord,
    updateRecordVersion,
    deleteRecordVersion,
    createRecordVersion,
    activeTariff,
    type,
    total,
  } = props;
  const dispatch = useDispatch();
  const [modal, contextHolder] = Modal.useModal();
  const [visible, setVisible] = useState(false);
  const [newTariffVisible, setNewTariffVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [editVersionVisible, setEditVersionVisible] = useState(false);
  const [newVersionVisible, setNewVersionVisible] = useState(false);
  const [order, setOrder] = useState("descend");
  const [field, setField] = useState("name");
  const [fetchPageSize, setFetchPageSize] = useState(10);
  const [filtersConditions, setFiltersConditions] = useState([
    { active: true },
  ]);

  const [currentNumber, setCurrentNumber] = useState(1);

  const intl = useIntl();

  const getParams = () => ({
    current: currentNumber,
    pageSize: fetchPageSize,
    order: {
      field,
      order,
    },
    filter: {
      $or: filtersConditions,
    },
  });

  useEffect(() => {
    dispatch(fetchPage(getParams()));
  }, [dispatch, currentNumber, fetchPageSize, filtersConditions, field, order]);

  const onChange = (
    { current, pageSize },
    filters,
    { field, order },
    extra
  ) => {
    const filtersArr = Object.entries(filters)
      .filter(([key, values]) => values && values.length > 0)
      .flatMap(([key, values]) =>
        values.map((value) => ({
          [key]: value,
        }))
      );

    setOrder(order);
    setField(field);
    setCurrentNumber(current);
    setFetchPageSize(pageSize);
    setFiltersConditions(filtersArr);
  };

  const pagination = {
    current: currentNumber,
    pageSize: fetchPageSize,
    total: total,
    pageSizeOptions: [10, 50, 100],
    showSizeChanger: true,
  };

  const onAddClick = () => {
    setVisible(true);
    setNewTariffVisible(true);
  };

  const onEdit = (record) => {
    dispatch(loadRecord(record.id));
    setVisible(true);
    setEditVisible(true);
  };

  const onEditVersion = (record) => {
    dispatch(loadRecord(record.id));
    setVisible(true);
    setEditVersionVisible(true);
  };

  const onAddVersion = (record) => {
    dispatch(loadRecord(record.id));
    setVisible(true);
    setNewVersionVisible(true);
  };

  const onDeleteVersion = (record) => {
    const title = intl.formatMessage({
      id: "app.tariffs.deleteTariffTitle",
    });
    const content = intl.formatMessage({
      id: "app.tariffs.deleteTariffContent",
    });
    modal.confirm({
      title: title,
      icon: <ExclamationCircleOutlined />,
      content: content,
      onOk() {
        return onConfirm(record);
      },
      onCancel() {},
    });
  };
  const onConfirm = async (record) => {
    const action = await dispatch(deleteRecord(record.id));
    if (!action.error) {
      dispatch(fetchPage(getParams()));
    }
  };

  const handleAdminMenuClick = (e, record) => {
    switch (e.key) {
      case "1":
        onEdit(record);
        break;

      case "2":
        onEditVersion(record);
        break;

      case "3":
        onAddVersion(record);
        break;
      case "4":
        onDeleteVersion(record);
        break;

      default:
        break;
    }
  };

  const hide = () => {
    setVisible(false);
    setNewTariffVisible(false);
    setEditVisible(false);
    setEditVersionVisible(false);
    setNewVersionVisible(false);
    dispatch(fetchPage(getParams()));
    dispatch(reset());
  };

  return (
    <div>
      {contextHolder}
      <FormHeaderWrapper>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Title level={5} style={{ margin: 0 }}>
            <FormattedMessage
              id={
                type === "wage"
                  ? `app.settings.tariffs`
                  : `app.settings.clientsTariffs`
              }
            />
          </Title>
          <RestrictedComponent
            resource={"organization.settings"}
            action={"editor"}
            comp={null}
          >
            <Button type="primary" onClick={onAddClick}>
              <FormattedMessage id={`app.actions.addTariff`} />
            </Button>
          </RestrictedComponent>
        </div>
      </FormHeaderWrapper>

      <Table
        bordered
        rowKey={(record) => record.id}
        scroll={{ y: window.innerHeight - 526 }}
        dataSource={items}
        pagination={pagination}
        onChange={onChange}
        columns={[
          {
            title: intl.formatMessage({
              id: "app.tariffs.name",
            }),
            dataIndex: "name",
            render(text, record) {
              return (
                <div style={{ display: "flex" }}>
                  <Text>{text}</Text>
                </div>
              );
            },
            sortDirections: ["ascend", "descend", "ascend"],
            defaultSortOrder: "descend",
            // sorter: (a, b) => a.name.localeCompare(b.name),
            sorter: true,
          },
          {
            title: intl.formatMessage({
              id: "app.tariffs.description",
            }),
            dataIndex: "description",
            render(text, record) {
              return (
                <div style={{ display: "flex" }}>
                  <Text>{text}</Text>
                </div>
              );
            },
          },

          {
            title: intl.formatMessage({
              id:
                type === "wage"
                  ? `app.tariffs.base_rate`
                  : `app.tariffs.clients_base_rate`,
            }),
            dataIndex: "cost",
            width: "100px",
            sortDirections: ["ascend", "descend", "ascend"],
            defaultSortOrder: "descend",
            sorter: true,
            render(text) {
              return (
                <div style={{ display: "flex" }}>
                  <Text>{text}</Text>
                </div>
              );
            },
          },
          {
            title: intl.formatMessage({
              id: "app.tariffs.effective_from",
            }),
            dataIndex: "tariff_versions",
            render: (text, record, index) => {
              //filter future effective versions
              const filtered = text.filter(
                (i) => moment(i.effective_from) < moment()
              );
              const selected =
                filtered.length > 1
                  ? moment(
                      filtered.reduce((a, b) =>
                        a.effective_from > b.effective_from ? a : b
                      ).effective_from
                    ).format("MM/YYYY")
                  : intl.formatMessage({
                      id: "app.tariffs.initial",
                    });
              return <Text>{selected}</Text>;
            },
          },
          {
            title: intl.formatMessage({
              id: "app.tariffs.active",
            }),
            dataIndex: "active",
            render: (text) => {
              return text && <CheckCircleOutlined />;
            },
            filters: [
              {
                text: intl.formatMessage({ id: "app.tariffs.active" }),
                value: true,
              },
              {
                text: intl.formatMessage({ id: "app.tariffs.inactive" }),
                value: false,
              },
            ],
            defaultFilteredValue: ["true"],
            width: "70px",
          },
          {
            title: intl.formatMessage({
              id: "app.tariffs.is_default",
            }),
            dataIndex: "is_default",
            render: (text, record, index) => {
              //filter future effective versions

              return (
                text && (
                  <Tag color="green">
                    {intl.formatMessage({
                      id: "app.tariffs.is_default",
                    })}
                  </Tag>
                )
              );
            },
          },

          {
            title: intl.formatMessage({
              id: "app.tariffs.actions",
            }),
            width: "130px",
            render(text, record) {
              const items = [
                {
                  label: intl.formatMessage({
                    id: "app.tariffs.editTariff",
                  }),
                  key: "1",
                  icon: <EditOutlined />,
                },
                {
                  label: intl.formatMessage({
                    id: "app.tariffs.editTariffVersion",
                  }),
                  key: "2",
                  icon: <EditOutlined />,
                },
                {
                  label: intl.formatMessage({
                    id: "app.tariffs.updateTariff",
                  }),
                  key: "3",
                  icon: <PlusOutlined />,
                },
                {
                  label: intl.formatMessage({
                    id: "app.tariffs.deleteTariff",
                  }),
                  key: "4",
                  icon: <DeleteOutlined />,
                  style: { color: "red" },
                },
              ];
              const menu = {
                items,
                onClick: (e) => handleAdminMenuClick(e, record),
              };

              return (
                <RestrictedComponent
                  resource={"organization.settings"}
                  action={"editor"}
                  comp={null}
                >
                  <div style={{ display: "flex" }}>
                    <Dropdown trigger={["click"]} menu={menu}>
                      <Button type="link">
                        {intl.formatMessage({
                          id: "app.employees.options",
                        })}
                        <DownOutlined />
                      </Button>
                    </Dropdown>
                  </div>
                </RestrictedComponent>
              );
            },
          },
        ]}
        loading={loading}
      />
      <Modal
        closable={false}
        maskClosable={false}
        open={visible}
        footer={null}
        onCancel={hide}
        title={null}
        width={"fit-content"}
        styles={{
          body: {
            padding: 0,
          },
        }}
        destroyOnClose={true}
      >
        {newTariffVisible ? (
          <NewTariff
            createRecord={createRecord}
            hide={hide}
            loading={changeLoading}
            addText={<FormattedMessage id="app.tariffs.addNewTariff" />}
            type={type}
          />
        ) : null}
        {editVisible ? (
          <EditTariff
            updateRecord={updateRecord}
            activeTariff={activeTariff}
            loading={changeLoading}
            hide={hide}
            addText={<FormattedMessage id="app.tariffs.editTariff" />}
          />
        ) : null}

        {editVersionVisible ? (
          <EditTariffVersion
            updateRecordVersion={updateRecordVersion}
            deleteRecordVersion={deleteRecordVersion}
            loading={changeLoading}
            activeTariff={activeTariff}
            hide={hide}
            addText={<FormattedMessage id="app.tariffs.editTariffVersion" />}
          />
        ) : null}

        {newVersionVisible ? (
          <NewVersion
            createRecordVersion={createRecordVersion}
            activeTariff={activeTariff}
            loading={changeLoading}
            hide={hide}
            addText={<FormattedMessage id="app.tariffs.updateTariff" />}
          />
        ) : null}
      </Modal>
    </div>
  );
};
export default TariffsList;
